import axios from "axios";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { languageOptions, workOrderStatus } from "./variables";
import {
  CustomerInvoiceStatusLabel,
  InvoiceStatusLabel,
  UserProfileStatus,
  UserType,
} from "./enum";

import * as Yup from "yup";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration.js";
dayjs.extend(duration);

export function setDataInLocalStorage(key, payload) {
  localStorage.setItem(key, JSON.stringify(payload));
}

export function fetchDataFromLocalStorage(key) {
  return JSON.parse(localStorage.getItem(key));
}

export function navigateWithReload(path) {
  window.location.href = path; // Navigate to the new route with a full reload
}

const PERSISTED_LOCAL_STORAGE_KEYS = /ColumnSettings:/;

export function clearAuthStorage() {
  Object.keys(window.localStorage)
    .filter((key) => !PERSISTED_LOCAL_STORAGE_KEYS.test(key))
    .forEach((key) => window.localStorage.removeItem(key));
}

/**
 * Work order detail links for a customer carry `fromCustomerBoard=true`, which
 * switches the page to the customer-facing view (their status and rates). Notification
 * urls are built server-side without it, so tag them on the way through.
 */
export function withCustomerBoardParam(url, isCustomer) {
  if (!isCustomer || !url) return url;
  if (!url.includes("/workOrder/details/") || url.includes("fromCustomerBoard")) return url;

  return `${url}${url.includes("?") ? "&" : "?"}fromCustomerBoard=true`;
}

export function logout() {
  const refreshToken = fetchDataFromLocalStorage("refreshToken");
  axios
    .post(`${process.env.REACT_APP_BASE_URL}auth/logout`, {
      refreshToken,
    })
    // Telling the server is best-effort: a session that is already inactive (the user
    // was deactivated or their access deleted) rejects this call, and the client must
    // still log out locally — otherwise the page just keeps retrying.
    .catch(() => {})
    .finally(() => {
      clearAuthStorage();
      navigateWithReload("/login");
    });
}

export function debounce(func, delay) {
  let timeoutId;

  return (...args) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}
export function getCurrentTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function handleGetLocation() {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Resolve the promise with the position
          resolve(position.coords);
        },
        (error) => {
          axios
            .get("https://ipinfo.io/json?token=e77319841bbdfe")
            .then((res) => {
              const [latitude, longitude] = res?.data?.loc?.split(",");
              resolve({ latitude, longitude });
            })
            .catch(() => {
              // If fallback fails
              reject(new Error("Failed to retrieve location from IP."));
            });

          /* switch (error.code) {
            case error.PERMISSION_DENIED:
              reject(new Error("Please allow location."));
              break;
            case error.POSITION_UNAVAILABLE:
              reject(new Error("Location information is unavailable."));
              break;
            case error.TIMEOUT:
              reject(new Error("The request to get user location timed out."));
              break;
            case error.UNKNOWN_ERROR:
              reject(new Error("An unknown error occurred."));
              break;
            default:
              reject(new Error("An unknown error occurred."));
          } */
        },
      );
    } else {
      // If the browser doesn't support Geolocation

      reject(new Error("Geolocation is not supported by this browser."));
    }
  });
}

export function calculateNewEstimatedHourlyTotal(updatedExpenses, hourlyRate, minimumHours) {
  const totalNewExpenses = updatedExpenses?.reduce(
    (sum, expense) => sum + parseFloat(expense?.amount || 0),
    0,
  );

  const totalLabourCost = parseFloat(hourlyRate || 0) * parseFloat(minimumHours || 0);

  return totalNewExpenses + totalLabourCost;
}
export function calculateNewEstimatedFullHalfDayTotal(updatedExpenses, fullHalfDayRate) {
  const totalNewExpenses = updatedExpenses?.reduce(
    (sum, expense) => sum + parseFloat(expense?.amount || 0),
    0,
  );

  return totalNewExpenses + fullHalfDayRate;
}

export function openInGoogleMaps(latitude, longitude) {
  const url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
  window.open(url, "_blank"); // Opens in a new tab or Google Maps app (if on mobile)
}

export const autoLinkText = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.split(urlRegex).map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
        >
          {part}
        </a>
      );
    } else {
      return <span key={index}>{part}</span>;
    }
  });
};

export const getFileExtension = (filename) => filename?.split(".").pop()?.toLowerCase();

export const isImage = (filename) => {
  const ext = getFileExtension(filename);
  return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
};

export const getFileIconClass = (filename) => {
  const ext = getFileExtension(filename);
  if (ext === "pdf") return "fa-file-pdf text-red-500";
  if (["doc", "docx"].includes(ext)) return "fa-file-word text-blue-500";
  if (["xls", "xlsx", "xlsm", "csv"].includes(ext)) return "fa-file-excel text-green-500";
  return "fa-file-lines text-gray-500";
};

/**
 * Invoice status as the given reader should see it. A CUSTOMER account gets the
 * customer-facing wording — an internally VERIFIED invoice is the one that has
 * been generated for them, an UNPAID one the one that has been approved — so
 * pass the viewer's role wherever a customer may be reading. Everyone else, and
 * any call with no role, keeps the internal label.
 */
export const formatInvoiceStatus = (status, role) => {
  if (!status) return "";
  if (role === UserType.CUSTOMER && CustomerInvoiceStatusLabel[status]) {
    return CustomerInvoiceStatusLabel[status];
  }
  return (
    InvoiceStatusLabel[status] ||
    status
      .toString()
      .split(/[\s_]+/)
      .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`)
      .join(" ")
  );
};

export const getLanguageLabel = (value) => {
  return languageOptions.find((opt) => opt.value === value)?.label || value;
};

export const displayFormattedTotalTimelog = (totalHours) => {
  // Ensure totalHours is a valid number
  const hoursValue = Number(totalHours) || 0;

  // Create a valid duration
  const dur = dayjs.duration(hoursValue, "hours");
  let hours = Math.floor(dur.asHours());
  let minutes = dur.minutes();
  const seconds = dur.seconds();
  if (seconds > 29) {
    minutes += 1;
    if (minutes === 60) {
      hours += 1;
      minutes = 0;
    }
  }

  return `${hours} hour${hours !== 1 ? "s" : ""} ${minutes} min${minutes !== 1 ? "s" : ""}`;
};

// utils/timeConversions.js
export function toBase100Hours({ hours, minutes }) {
  if (hours == null || minutes == null) return 0;
  const decimalPart = (minutes / 60) * 100;
  return Number((hours + decimalPart / 100).toFixed(2));
}

export function formatTwoDecimals(value) {
  if (value === undefined || value === null || isNaN(value)) return "0.00";
  return (Math.floor(value * 100) / 100).toFixed(2);
}

export async function blobUrlToFile(blobUrl, fileName) {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type });
}

export const workOrderStatusIndex = workOrderStatus?.reduce((acc, status, index) => {
  acc[status] = index;
  return acc;
}, {});

export const isStatusBetween = (currentStatus, startStatus, endStatus) => {
  return (
    workOrderStatusIndex[currentStatus] >= workOrderStatusIndex[startStatus] &&
    workOrderStatusIndex[currentStatus] <= workOrderStatusIndex[endStatus]
  );
};

// The users whose chat belongs to the work order's assignment: the assigned
// engineer, the vendor (freelance company) owner, and the member engineer the
// vendor routed the work to. Empty when the work order is not assigned yet.
export function getAssignedChatUserIds(workOrderDetail) {
  const assignedEngineerId = workOrderDetail?.engineerId ?? workOrderDetail?.engineer?.id ?? null;

  if (!assignedEngineerId) return new Set();

  const allowedIds = new Set([assignedEngineerId]);

  // Vendor (freelance company) assignment: also allow the vendor/FC owner AND the
  // member engineer the vendor routed the work to (they are doing the actual work).
  // On the CLIENT work-order response `assignedFreelanceCompany`/`assignedFreelanceCompanyId`
  // are stripped by the response DTO — the vendor (with its owner) is surfaced as
  // `engineerCompany` instead, so read the owner from there first.
  const vendorOwnerId =
    workOrderDetail?.engineerCompany?.owner?.id ??
    workOrderDetail?.assignedFreelanceCompany?.owner?.id ??
    null;
  if (vendorOwnerId) {
    allowedIds.add(vendorOwnerId);

    // Some response shapes set `engineerId` to the FC owner; also unblur the routed
    // member engineer who does the actual work.
    const routedOffer = (workOrderDetail?.offers ?? []).find(
      (o) =>
        ["ROUTED", "ACCEPTED", "ASSIGNED"].includes(o?.status) &&
        o?.user?.id !== vendorOwnerId &&
        o?.createdById === vendorOwnerId,
    );
    if (routedOffer?.user?.id) allowedIds.add(routedOffer.user.id);
  }

  return allowedIds;
}

export function getChatUserBlurPredicate(workOrderDetail) {
  const allowedIds = getAssignedChatUserIds(workOrderDetail);

  // Not assigned yet -> nothing is blurred.
  if (allowedIds.size === 0) return () => false;

  return (user) => !user?.isCustomerChat && !allowedIds.has(user?.id);
}

// Which conversation the chat tab opens by default. The customer↔team room now
// exists from the moment a customer raises the work order and is listed even with
// no messages, so it can sit ahead of the assigned engineer in the (recent-message
// ordered) list. Once assigned, the assigned engineer/vendor chat is the one to
// open; the customer chat stays in the list, just not auto-selected. Falls back to
// the first entry — the previous behaviour — when nothing is assigned yet.
export function pickDefaultChatUser(chatUsers, workOrderDetail) {
  if (!chatUsers?.length) return null;

  const assignedIds = getAssignedChatUserIds(workOrderDetail);
  const assignedEntry = assignedIds.size
    ? chatUsers.find((user) => assignedIds.has(user?.id))
    : null;

  return assignedEntry ?? chatUsers[0];
}

// Which side of the deal added an expense, as the viewer sees it. A customer
// counts their client as the provider; a client or engineer counts the customer
// as a buyer alongside the client.
export function expenseSideLabel(addedByRole, viewerRole) {
  if (viewerRole === UserType.CUSTOMER) {
    return addedByRole === UserType.CUSTOMER ? "Buyer" : "Provider";
  }
  return addedByRole === UserType.CLIENT || addedByRole === UserType.CUSTOMER
    ? "Buyer"
    : "Provider";
}

// Customer Rates is what the customer pays the client, so there the customer is the
// buyer and the client the provider — the same for whoever is looking. An expense
// that came in through the client's counter offer is stamped with the customer once
// accepted, so it is told apart by `isFromCounterOffer` (the expense's offerId).
export function customerRatesExpenseSideLabel(addedByRole, isFromCounterOffer = false) {
  return addedByRole === UserType.CUSTOMER && !isFromCounterOffer ? "Buyer" : "Provider";
}

export function formatExpenseLabel(expense) {
  const type = expense?.expenseType;
  const name = type ? `${type.charAt(0)}${type.slice(1).toLowerCase()} Expense` : "Expense";
  const description = expense?.description?.trim();

  return description ? `${name} (${description})` : name;
}

export const IMAGE_MAX_UPLOAD_SIZE = 10 * 1024 * 1024;
export const FILE_MAX_UPLOAD_SIZE = 2 * 1024 * 1024;

export function isCompressibleImage(file) {
  const type = file?.type ?? "";
  if (type === "image/gif" || type === "image/svg+xml") return false;
  return type.startsWith("image/");
}

export function maxUploadSizeFor(file) {
  return isCompressibleImage(file) ? IMAGE_MAX_UPLOAD_SIZE : FILE_MAX_UPLOAD_SIZE;
}

export function uploadSizeErrorMessage(file) {
  return isCompressibleImage(file)
    ? "Image must be 10MB or smaller"
    : "File must be 2MB or smaller";
}

export function validateUploadSize(value, ctx) {
  if (!value) return false;
  if (value.size <= maxUploadSizeFor(value)) return true;
  return ctx.createError({ message: uploadSizeErrorMessage(value) });
}

export function decodeHtmlEntities(value = "") {
  if (!value || !value.includes("&")) return value;
  const scratch = document.createElement("textarea");
  scratch.innerHTML = value;
  return scratch.value;
}

export function formatPhoneNumber(value, defaultCountry = "AU") {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  if (!/[1-9]/.test(raw)) return "";

  const withPlus = parsePhoneNumberFromString(raw.startsWith("+") ? raw : `+${raw}`);
  if (withPlus) return withPlus.formatInternational();

  const national = parsePhoneNumberFromString(raw, defaultCountry);
  if (national?.isValid()) return national.formatInternational();

  return raw;
}

export function normalizePhoneNumber(value, country) {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return "";

  const dialCode = String(country?.dialCode ?? "").replace(/\D/g, "");
  if (dialCode && !digits.startsWith(dialCode)) return `+${dialCode}${digits}`;

  return `+${digits}`;
}

export function customerLabel(authRole) {
  return authRole === UserType.CUSTOMER ? "Company" : "Customer";
}

// Rich text (BaseEditor)

const HTML_TAG = /<\/?[a-z][\s\S]*>/i;

export const isHtml = (value) => HTML_TAG.test(value || "");

export const isRichTextEmpty = (html) =>
  !(html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

export const normalizeRichText = (html) => (isRichTextEmpty(html) ? "" : html);

const escapeHtml = (text) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const toEditorHtml = (value) => {
  if (!value || isHtml(value)) return value || "";
  return value
    .split("\n")
    .map((line) => `<p>${escapeHtml(line) || "<br>"}</p>`)
    .join("");
};

// Customers and entities

export const ANNUAL_REVENUE_OPTIONS = [
  { value: "1", label: "Under $1 Million" },
  { value: "5-10", label: "$5 - $10 Million" },
  { value: "11", label: "Above $10 Million" },
];

export const annualRevenueLabel = (value) =>
  ANNUAL_REVENUE_OPTIONS.find((option) => option.value === String(value))?.label || value;

// The entity created with a self-signup customer, whose registration and accounts it shows. Client-created customers have none.
export const getDefaultEntity = (customer) =>
  customer?.defaultEntityId
    ? customer.childClients?.find((child) => child.id === customer.defaultEntityId)
    : undefined;

export const isSelfSignup = (customer) =>
  !!customer?.createdById && customer.createdById === customer.ownerId;

export const buildClientFormData = (values) => {
  const formData = new FormData();
  formData.append("clientName", values.clientName);
  formData.append("managerName", values.manager);
  formData.append("email", values.email);
  formData.append("teamEmail", values.teamEmail || "");
  formData.append("phoneNumber", values.phone);
  formData.append("website", values.website);
  if (values.companyAnnualRevenue) {
    formData.append("companyAnnualRevenue", values.companyAnnualRevenue);
  }
  formData.append("policies", values.policies);
  formData.append("country", values.country);
  formData.append("addressLine1", values.address);
  formData.append("addressLine2", "");
  formData.append("city", values.city);
  formData.append("state", values.state);
  formData.append("zipCode", values.zipCode);
  formData.append("latitude", values.latitude);
  formData.append("longitude", values.longitude);
  if (values.branchId) formData.append("branchId", values.branchId);
  if (values.teamId) formData.append("teamId", values.teamId);
  if (values.secondaryTeamId) formData.append("secondaryTeamId", values.secondaryTeamId);
  formData.append("accountsPerson", values.accountsPerson || "");
  formData.append("accountsEmail", values.accountsEmail || values.email);
  formData.append("accountsPhone", values.accountsPhone || "");
  if (values.paymentTerms) {
    formData.append("paymentTerms", values.paymentTerms);
  }

  formData.append("taxType", values.taxType || "");
  formData.append("taxPercentage", values.taxPercentage ?? "");
  formData.append("taxRegistrationNumber", values.taxRegistrationNumber || "");
  formData.append("registrationNumber", values.registrationNumber || "");

  if (values.clientLogo) {
    formData.append("clientLogoFile", values.clientLogo);
  }
  if (values.registrationCertificate instanceof File) {
    formData.append("registrationCertificateFile", values.registrationCertificate);
  }

  return formData;
};

// The keys a form field is sent under, where they differ from its name.
const FORM_DATA_KEYS = {
  manager: ["managerName"],
  phone: ["phoneNumber"],
  address: ["addressLine1", "addressLine2", "latitude", "longitude"],
  clientLogo: ["clientLogoFile"],
  registrationCertificate: ["registrationCertificateFile"],
};

// Keeps only the given form fields, so a card saves its own fields and nothing else.
export const pickFormData = (formData, fields = []) => {
  const keys = fields.flatMap((field) => FORM_DATA_KEYS[field] || [field]);
  const picked = new FormData();
  for (const [key, value] of formData.entries()) {
    if (keys.includes(key)) picked.append(key, value);
  }
  return picked;
};

// A customer editing its own company

export const ownCustomerValidationSchema = Yup.object({
  clientName: Yup.string().required("Company Name is required"),
  address: Yup.string().required("Address is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().notRequired(),
  zipCode: Yup.string().required("Zip Code is required"),
  country: Yup.string().required("Country is required"),
  phone: Yup.string().required("Phone is required"),
  email: Yup.string()
    .required("Email is required")
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email format"),
  website: Yup.string().notRequired(),
  companyAnnualRevenue: Yup.string().required("Annual Revenue is required"),
  manager: Yup.string().required("Contact Person is required"),
  registrationNumber: Yup.string().trim().required("Registration Number is required"),
  registrationCertificate: Yup.mixed().required("Registration Certificate is required"),
});

export const ownCustomerInitialValues = (customer) => {
  const entity = getDefaultEntity(customer);

  return {
    clientName: customer?.clientName || "",
    manager: entity?.managerName || "",
    registrationNumber: entity?.registrationNumber || "",
    registrationCertificate: entity?.registrationCertificate || null,
    taxRegistrationNumber: entity?.taxRegistrationNumber || "",
    address: customer?.address?.addressLine1 || "",
    city: customer?.address?.city || "",
    state: customer?.address?.state || "",
    zipCode: customer?.address?.zipCode || "",
    country: customer?.address?.country || "",
    phone: customer?.phoneNumber || "",
    email: customer?.email || "",
    teamEmail: customer?.teamEmail || "",
    website: customer?.website || "",
    policies: customer?.policies || "",
    latitude: customer?.address?.latitude || "",
    longitude: customer?.address?.longitude || "",
    clientLogo: customer?.clientLogo || null,
    companyAnnualRevenue: customer?.companyAnnualRevenue || "",
  };
};

export const buildOwnCustomerFormData = (values) => {
  const formData = new FormData();
  formData.append("clientName", values.clientName);
  formData.append("managerName", values.manager || "");
  formData.append("email", values.email);
  formData.append("phoneNumber", values.phone);
  formData.append("website", values.website || "");
  formData.append("policies", values.policies || "");
  formData.append("country", values.country);
  formData.append("addressLine1", values.address);
  formData.append("addressLine2", "");
  formData.append("city", values.city);
  formData.append("state", values.state || "");
  formData.append("zipCode", values.zipCode);
  formData.append("latitude", values.latitude || "");
  formData.append("longitude", values.longitude || "");
  formData.append("registrationNumber", values.registrationNumber || "");
  formData.append("taxRegistrationNumber", values.taxRegistrationNumber || "");
  if (values.clientLogo instanceof File) {
    formData.append("clientLogoFile", values.clientLogo);
  }
  if (values.registrationCertificate instanceof File) {
    formData.append("registrationCertificateFile", values.registrationCertificate);
  }
  return formData;
};

export const formatRating = (rating) => {
  if (rating === null || rating === undefined || rating === "" || isNaN(Number(rating))) return "";

  return Number(rating).toFixed(2);
};

export const isVendorTech = (user) => !!user?.isCompanyMember;

// Nationality and ID documents are the identity an admin signed off on, so they
// freeze once an engineer's profile is approved — individual engineers, vendor
// engineers and vendor owners alike. The backend enforces the same rule.
export function isIdentityLocked(user) {
  return (
    user?.role?.role === UserType.FREELANCER &&
    [UserProfileStatus.APPROVED, UserProfileStatus.ACTIVATED].includes(user?.status)
  );
}
